import { createSelector } from "reselect";
import { get, cloneDeep } from "lodash";
import * as yup from "yup";
import { DATA_TYPE_DROPDOWN2, DATA_TYPE_HYPERLINK, DATA_TYPE_GEOCODE } from "typeConstants/dataTypes";

const SURVEY_TYPE = "CaseInfo.CurrentInspectionType";
const POLICY_HOLDER_EMAIL = "PolicyInfo.PolicyHolderInfo.PolicyholderEmail";
const YEAR_BUILT_IN = "CaseInfo.YearBuiltIn";
const CUSTOMER_NUMBER = "PolicyInfo.CustomerInfo.CustomerNumber";
const SURVEY_COUNTY = "PolicyInfo.SurveyAddress.County";
const GEOCODE = "PolicyInfo.GeoCode";

const validators = {
  [POLICY_HOLDER_EMAIL]: yup.string().nullable().email("Email address is not valid"),
  [YEAR_BUILT_IN]: yup.number().typeError("Year Built In has to be a number").positive("Number must be positive").min(1000, "Must be atleast 4 digits").max(9999, "Must not be more than 4 digits")
};

const getValueFromDto = (field, dto) => {
  switch (field.dataType) {
    case DATA_TYPE_DROPDOWN2:
      return getValueForDataTypeDropdown(field.key, dto);
    case DATA_TYPE_HYPERLINK:
      return getValueFromDataTypeHyperlink(field.key, dto);
    case DATA_TYPE_GEOCODE:
      return getValueFromDataTypeGeocode(field.key, dto);
    default:
      return getValueForDefaultDataType(field.key, dto);
  }
};

const getValueForDefaultDataType = (key, dto) => typeof (key) === "string" ? key.split(".").reduce((acc, cur) => acc ? acc[convertToCamelCase(cur)] : undefined, dto) : null;

// trying to make a convert to camel case function that matches newtonsofts way (from c#), because CaseHeaderV2 sends keys back from the server in initial case which is dumb, and we use those keys to get values off of our dtos for the MCards
// first try we just lowercased the first letter but that was bad for stuff like QATimeLog, we would return qATimeLog, but newtonsoft did qaTimeLog
// then we switched to lodash which handled that right but didnt resolve things like ClientETA properly, lodash did clientEta, but newtonsoft did clientETA
// this function was hand coded to hopefully match newtonsoft perfectly
const convertToCamelCase = str => {
  if (!str || str === "" || typeof str !== "string") return str;

  let firstLower = [...str].findIndex(s => s !== s.toUpperCase());

  if (firstLower > 1) return str.substring(0, firstLower - 1).toLowerCase() + str.substring(firstLower - 1);
  else return str[0].toLowerCase() + str.substring(1);
};


const getValueForDataTypeDropdown = (key, dto) => {
  switch (key) {
    case SURVEY_TYPE:
      return { label: get(dto, ["caseInfo", "currentInspectionType", "name"]), value: get(dto, ["caseInfo", "currentInspectionType", "Id"]) };
    case CUSTOMER_NUMBER:
      return {
        label: `${get(dto, ["policyInfo", "customerInfo", "customerName"])} (${get(dto, ["policyInfo", "customerInfo", "customerNumber"])})`,
        value: get(dto, ["policyInfo", "customerInfo", "customerNumber"])
      };
    default:
      return { label: getValueForDefaultDataType(key, dto), value: getValueForDefaultDataType(key, dto) };
  }
};

const getValueFromDataTypeHyperlink = (key, dto) => {
  switch (key) {
    case SURVEY_COUNTY:
      return {
        label: `${get(dto, ["policyInfo", "surveyAddress", "county"])}`,
        onClick: `http://maps.google.com/?q=${get(dto, ["policyInfo", "surveyAddress", "county"])}%20county,%20${get(dto, ["policyInfo", "surveyAddress", "state"])}`
      };
    default:
      return getValueForDefaultDataType(key, dto);
  }
};

const getValueFromDataTypeGeocode = (key, dto) => {
  switch (key) {
    case GEOCODE:
      return `${get(dto, ["policyInfo", "geoCode", "latitude"])}, ${get(dto, ["policyInfo", "geoCode", "longitude"])}`;
    default:
      return getValueForDefaultDataType(key, dto);
  }
};

export const insertValueFieldIntoCards = (caseHeaderView) => {
  let caseHeader = { ...caseHeaderView };
  caseHeader.cards.forEach(c => c.fields.forEach(f => {
    f.value = getValueFromDto(f, caseHeaderView.caseHeaderDto);
    f.editFields && f.editFields.forEach(ef => ef.value = getValueFromDto(ef, caseHeaderView.caseHeaderDto));
    f.tooltip && f.tooltip.fields.forEach(tt => tt.value = getValueFromDto(tt, caseHeaderView.caseHeaderDto));
  }));
  return caseHeader;
};

const addValidation = cards => {
  Object.keys(validators).forEach(k => {
    let matchedField = cards.reduce((a, c) => a = [...a, ...c.fields], []).find(f => f.key === k);
    if (matchedField) {
      matchedField.validation = validators[k];
    }
  });
};

const getCaseHeaderV2 = state => state.caseForms.caseHeaderV2;

export const caseHeaderV2Selector = createSelector(
  [getCaseHeaderV2],
  caseHeaderV2 => {
    if (caseHeaderV2) {
      let updated = cloneDeep(caseHeaderV2);
      insertValueFieldIntoCards(updated);
      addValidation(updated.cards);
      return updated;
    }
  }
);
